import { useCallback, useEffect, useMemo, useRef } from "react"

import { isRTableContent } from "./elementData"
import useReportingDataLoading, {
  ScriptExecutionResult,
} from "../DataLoading/useReportingDataLoading"
import {
  useElementState,
  ElementState,
  ElementConfigState,
} from "./useElementState"
import { UseElementConfigReturn } from "./useElementConfig"

import {
  convertDataToTable,
  TransformedTableData,
} from "utils/convertDataToTable"
import { flattenCodebookForSelect } from "utils/codebookUtils"

import {
  ELEMENT_TYPE,
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { SelectOption } from "types/ui.types"
import { ElementRef } from "../../../../features/Reporting/components/ReportingDisplay/ReportingDisplay.component"

/**
 * Element size update payload shared by all element types.
 */
export interface ElementSizeUpdate {
  width?: number
  height?: number
}

/**
 * Superset of the props the four public element hooks receive. Each hook
 * keeps its own (consumer-facing) props interface and passes it through —
 * the shapes are structurally compatible.
 */
export interface BaseElementProps {
  /** Global filter string passed to data elements (absent for time series). */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: ElementSizeUpdate) => void
  /** Element configuration */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Latest-render snapshot of the base internals handed to every adapter
 * callback. The base keeps it in a ref that is rebuilt each render, so the
 * (stable) queued executor always sees current state at run time — this is
 * what makes the 2026-07-02 transpose queue-routing decision state-safe.
 */
export interface ElementBaseContext<TData> {
  /** The props object as passed to the public hook (fresh each render). */
  props: BaseElementProps
  element: SlideElement
  filter: string | undefined
  isDashboard: boolean
  queryInstance: QueryInstance | null

  // External boundary: engine data loading
  executeScript: (
    script: string,
    elementId?: string
  ) => Promise<ScriptExecutionResult>
  isLoading: boolean
  error: string | null
  clearError: () => void

  // Config management
  configHook: UseElementConfigReturn
  /** Shorthand for configHook.getConfigValue. */
  getValue: <T = unknown>(key: string, fallback?: T) => T

  // Element state (useElementState)
  data: TData | null
  showSelects: boolean
  hasAttemptedLoad: boolean
  hasProcessedData: boolean
  isQueuedForExecution: boolean
  setData: (data: TData | null) => void
  updateElementState: (updates: Partial<ElementState<TData>>) => void
  updateConfigState: (updates: Partial<ElementConfigState>) => void
  markAttemptedLoad: () => void
  setQueuedForExecution: (isQueued: boolean) => void
  resetElement: () => void

  // Execution (base wrapper — includes the TS direct-execute fallback)
  requestSequentialExecution: (reason?: string) => void
}

/**
 * Per-element-type adapter contract for useElementBase.
 *
 * Where the four hooks genuinely diverge (validation ordering, persistence
 * timing, image-effect guards — see the characterization findings in
 * docs/refactoring/element-hook-factory-design.md), the divergent logic
 * lives HERE (or in the hook file), not in the base: parity beats purity.
 *
 * Adapter objects may be rebuilt every render (Table/Pie close over
 * hook-file refs and state); the base only ever reads them through a ref.
 */
export interface ElementAdapter<TData> {
  /** Element type registered with useElementState. */
  elementType: ELEMENT_TYPE
  /** Passed to useReportingDataLoading (array payloads + polling shape). */
  isTimeSeries: boolean
  /** Per-type config hook (from useElementConfigFactory). */
  useConfigHook: (options: {
    elementId: string
    config: DataElementConfig | null | undefined
    onConfigUpdate: (config: DataElementConfig | null) => void
    autoSave?: boolean
  }) => UseElementConfigReturn
  /** Default element dimensions restored by resetSelection. */
  defaultSize: { width: number; height: number }
  /**
   * Whether the global dashboard filter triggers re-execution. TS: false —
   * DECIDED (2026-07-02, risk item 7): the wave variable IS the filter
   * dimension for time series.
   */
  reactsToFilter: boolean
  /**
   * TS only: execute directly when no onElementExecutionRequest parent is
   * wired. The useElementState-based siblings silently no-op instead
   * (both behaviors pinned by the characterization tests).
   */
  directExecuteWhenUnmanaged?: boolean
  /** Initial data slot for useElementState (TS: the non-null empty view). */
  initialData?: TData | null
  /**
   * Enable useElementState's internal auto-run. Disarmed in practice for
   * Chart/Pie/Table (the config-loading effect's setData(null) sets
   * hasProcessedData true before initialization settles — see the
   * "Risk 1 is latent, not live" characterization finding); TS opts out
   * explicitly. The hook-level auto-run effect below is the single owner
   * of the "auto_run" reason.
   */
  internalAutoRunEnabled: boolean
  /** Conditions for the (disarmed) internal auto-run — kept for parity. */
  internalAutoRunConditions?: (ctx: ElementBaseContext<TData>) => boolean
  /** Whether the base computes the shared RTable convertDataToTable memo. */
  convertsRTableData: boolean
  /** True when a loaded config has enough selections to leave selector mode. */
  configShowsData: (config: DataElementConfig) => boolean
  /** Reset the data slot when a config (or none) loads. */
  resetDataForConfigLoad: (ctx: ElementBaseContext<TData>) => void
  /** Extra work when a config loads (Table: originalWidth restore). */
  onConfigLoaded?: (
    ctx: ElementBaseContext<TData>,
    config: DataElementConfig | null
  ) => void
  /** All required variables selected (hook-level auto-run gate). */
  hasRequiredSelections: (ctx: ElementBaseContext<TData>) => boolean
  /**
   * Value-level signature of the required selections. Participates in the
   * auto-run effect deps so selection-value changes retrigger it (mirrors
   * the old hooks' raw config-value dependency arrays).
   */
  autoRunSignature: (ctx: ElementBaseContext<TData>) => string
  /** Whether the data slot still needs a load (TS keys off the converted view). */
  isDataEmptyForAutoRun?: (data: TData | null) => boolean
  /** Imperative-handle predicate exposed to the parent. */
  hasDataAndShouldRefresh: (ctx: ElementBaseContext<TData>) => boolean
  /**
   * THE per-type executor. Validation ordering, dashboard-defaults seeding
   * and cache side effects are intentionally NOT unified across types.
   */
  execute: (ctx: ElementBaseContext<TData>) => Promise<void>
  /** Per-type slice of resetSelection (runs before size restore + clearConfig). */
  resetState: (ctx: ElementBaseContext<TData>) => void
  /** After clearConfig in resetSelection (Table: extra onConfigUpdate(null) + cache clear). */
  onAfterReset?: (ctx: ElementBaseContext<TData>) => void
  /** Before the skip-flag/queue steps of handleRefresh (TS: hasProcessedData reset). */
  onBeforeRefresh?: (ctx: ElementBaseContext<TData>) => void
  /** After handleRefresh queues (Pie: closeSubmenus). */
  onAfterRefresh?: (ctx: ElementBaseContext<TData>) => void
  /** After handleEdit resets (Pie: closeSubmenus). */
  onAfterEdit?: (ctx: ElementBaseContext<TData>) => void
  /** After handleDelete delegates (Pie: closeSubmenus; Table: cache clear). */
  onAfterDelete?: (ctx: ElementBaseContext<TData>) => void
}

/**
 * Shared base for the four element hooks (Table/Chart/Pie/TimeSeries).
 *
 * Owns everything that is provably identical across the four types:
 * useReportingDataLoading wiring, config-hook wiring, useElementState wiring,
 * the RTable transform memo, the selectOptions memo, the executor
 * registration, the element-registration / config-loading / auto-run /
 * filter-change effects, and the handleRefresh/handleEdit/handleDelete/
 * resetSelection family. Everything type-specific arrives via the adapter.
 */
export const useElementBase = <TData = unknown>(
  props: BaseElementProps,
  adapter: ElementAdapter<TData>
) => {
  const {
    filter,
    element,
    codebook,
    queryInstance,
    config,
    onConfigUpdate,
    isDashboard = true,
    onDeleteRequest,
    onElementExecutionRequest,
    onRegisterElementRef,
  } = props

  // Latest-render refs: adapter callbacks and the stable executor read
  // through these, so a queued execution never runs against a stale closure.
  const adapterRef = useRef<ElementAdapter<TData>>(adapter)
  adapterRef.current = adapter
  const propsRef = useRef<BaseElementProps>(props)
  propsRef.current = props
  const ctxRef = useRef<ElementBaseContext<TData> | null>(null)

  // External boundary: engine data loading.
  const { executeScript, isLoading, error, clearError } =
    useReportingDataLoading(queryInstance, adapter.isTimeSeries)

  // Per-type config hook (autoSave off everywhere: persistence timing is a
  // per-type concern — image effects for Chart/Pie/TS, an explicit config
  // save effect for Table — and stays in the adapters/hook files).
  const configHook = adapter.useConfigHook({
    elementId: element.id,
    config,
    onConfigUpdate,
    autoSave: false,
  })

  const { setSkipNextConfigUpdate, isGeneratingImageRef } = configHook

  // Standardized element state management.
  const elementStateHook = useElementState<TData>({
    elementId: element.id,
    elementType: adapter.elementType,
    queryInstance,
    initialData: adapter.initialData ?? null,
    onExecutionRequest: onElementExecutionRequest,
    onElementUpdate: (id, updates) => {
      propsRef.current.onUpdate(id, updates as ElementSizeUpdate)
    },
    onDeleteRequest,
    autoRun: adapter.internalAutoRunEnabled
      ? {
          enabled: true,
          delay: 100,
          conditions: (): boolean =>
            ctxRef.current
              ? adapterRef.current.internalAutoRunConditions?.(
                  ctxRef.current
                ) ?? true
              : false,
        }
      : { enabled: false },
  })

  const {
    elementState: {
      data,
      hasAttemptedLoad,
      hasProcessedData,
      isQueuedForExecution,
    },
    showSelects,
    setData,
    updateElementState,
    updateConfigState,
    markAttemptedLoad,
    setQueuedForExecution,
    registerExecutor,
    requestSequentialExecution: requestElementExecution,
    resetElement,
  } = elementStateHook

  /**
   * Stable executor: delegates to the adapter with the freshest context.
   * The sequential queue captures this function at queue time; because it
   * reads ctxRef at RUN time, state written before queueing (e.g. the
   * flipped transpose value) is visible to the queued run.
   */
  const executeElement = useCallback(async (): Promise<void> => {
    if (!ctxRef.current) return
    await adapterRef.current.execute(ctxRef.current)
  }, [])

  /**
   * Queue wrapper. TS falls back to a DIRECT execution when no
   * onElementExecutionRequest parent is wired (pinned); the
   * useElementState-based path silently no-ops in that case instead.
   */
  const requestSequentialExecution = useCallback(
    (reason?: string): void => {
      if (
        !propsRef.current.onElementExecutionRequest &&
        adapterRef.current.directExecuteWhenUnmanaged
      ) {
        void executeElement()
        return
      }
      requestElementExecution(reason)
    },
    [executeElement, requestElementExecution]
  )

  // Shared RTable transform (single-sourced isRTableContent guard).
  const convertedData = useMemo((): TransformedTableData | null => {
    if (!adapterRef.current.convertsRTableData) return null
    const content = (data as { content?: unknown } | null)?.content
    if (!content) return null
    try {
      if (!isRTableContent(content)) {
        console.warn("Element data content is not RTableContent format")
        return null
      }
      return convertDataToTable(content)
    } catch (err) {
      console.error("Error converting element data:", err)
      return null
    }
  }, [data])

  // Memoized select options.
  const selectOptions = useMemo((): SelectOption[] => {
    try {
      return flattenCodebookForSelect(codebook)
    } catch {
      return []
    }
  }, [codebook])

  // Imperative-handle predicate (stable; evaluates against current state).
  const hasDataAndShouldRefresh = useCallback((): boolean => {
    return ctxRef.current
      ? adapterRef.current.hasDataAndShouldRefresh(ctxRef.current)
      : false
  }, [])

  // Handler family (with adapter pre/post callbacks).
  const handleRefresh = useCallback((): void => {
    if (ctxRef.current) {
      adapterRef.current.onBeforeRefresh?.(ctxRef.current)
    }
    setSkipNextConfigUpdate(true)
    requestSequentialExecution("user_refresh")
    if (ctxRef.current) {
      adapterRef.current.onAfterRefresh?.(ctxRef.current)
    }
  }, [setSkipNextConfigUpdate, requestSequentialExecution])

  const resetSelection = useCallback((): void => {
    const ctx = ctxRef.current
    if (!ctx) return
    const currentAdapter = adapterRef.current
    currentAdapter.resetState(ctx)
    ctx.props.onUpdate(ctx.element.id, {
      width: currentAdapter.defaultSize.width,
      height: currentAdapter.defaultSize.height,
    })
    ctx.configHook.clearConfig()
    currentAdapter.onAfterReset?.(ctx)
  }, [])

  const handleEdit = useCallback((): void => {
    resetSelection()
    if (ctxRef.current) {
      adapterRef.current.onAfterEdit?.(ctxRef.current)
    }
  }, [resetSelection])

  const handleDelete = useCallback((): void => {
    propsRef.current.onDeleteRequest?.()
    if (ctxRef.current) {
      adapterRef.current.onAfterDelete?.(ctxRef.current)
    }
  }, [])

  // Assemble the latest-render context (after all pieces exist).
  const ctx: ElementBaseContext<TData> = {
    props,
    element,
    filter,
    isDashboard,
    queryInstance,
    executeScript,
    isLoading,
    error,
    clearError,
    configHook,
    getValue: configHook.getConfigValue,
    data,
    showSelects,
    hasAttemptedLoad,
    hasProcessedData,
    isQueuedForExecution,
    setData,
    updateElementState,
    updateConfigState,
    markAttemptedLoad,
    setQueuedForExecution,
    resetElement,
    requestSequentialExecution,
  }
  ctxRef.current = ctx

  // Value-level auto-run gate: null while required selections are missing,
  // otherwise a signature of the selection values (so value changes
  // retrigger the effect, mirroring the old raw config-value deps).
  const autoRunKey = adapter.hasRequiredSelections(ctx)
    ? adapter.autoRunSignature(ctx)
    : null
  const isDataEmpty = adapter.isDataEmptyForAutoRun
    ? adapter.isDataEmptyForAutoRun(data)
    : !data

  // Register the (stable) executor with the element-state queue.
  useEffect(() => {
    registerExecutor(executeElement)
  }, [registerExecutor, executeElement])

  // Config loading effect.
  useEffect(() => {
    if (isGeneratingImageRef.current) return
    const currentCtx = ctxRef.current
    if (!currentCtx) return
    const currentAdapter = adapterRef.current

    if (config) {
      updateConfigState({
        showSelects: !currentAdapter.configShowsData(config),
      })
    } else {
      updateConfigState({ showSelects: true })
    }
    currentAdapter.resetDataForConfigLoad(currentCtx)
    clearError()
    currentAdapter.onConfigLoaded?.(currentCtx, config ?? null)
  }, [config, isGeneratingImageRef, clearError, updateConfigState])

  // Hook-level auto-run effect — the single owner of the "auto_run" reason
  // (useElementState's internal auto-run stays disarmed, see the adapter
  // contract notes).
  useEffect(() => {
    const needsData =
      autoRunKey !== null &&
      !showSelects &&
      isDataEmpty &&
      !isLoading &&
      !error

    if (needsData && queryInstance && !configHook.isInitializing) {
      setSkipNextConfigUpdate(true)
      const timer = setTimeout(() => {
        requestSequentialExecution("auto_run")
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [
    autoRunKey,
    showSelects,
    isDataEmpty,
    isLoading,
    error,
    queryInstance,
    configHook.isInitializing,
    requestSequentialExecution,
    setSkipNextConfigUpdate,
  ])

  // Filter-change effect (gated on the adapter's reactsToFilter — TS opts
  // out: for time series the wave variable IS the filter dimension).
  const prevFilterRef = useRef<string | undefined>(filter)
  useEffect(() => {
    if (!adapterRef.current.reactsToFilter) return
    if (filter !== prevFilterRef.current && data && !isLoading) {
      setSkipNextConfigUpdate(true)
      requestSequentialExecution("filter_change")
    }
    prevFilterRef.current = filter
  }, [
    filter,
    data,
    isLoading,
    requestSequentialExecution,
    setSkipNextConfigUpdate,
  ])

  // Element registration effect (imperative handle for the parent).
  useEffect(() => {
    if (onRegisterElementRef) {
      onRegisterElementRef(element.id, {
        hasDataAndShouldRefresh,
        requestSequentialExecution,
      })
    }

    return () => {
      if (onRegisterElementRef) {
        onRegisterElementRef(element.id, null)
      }
    }
  }, [
    element.id,
    onRegisterElementRef,
    hasDataAndShouldRefresh,
    requestSequentialExecution,
  ])

  return {
    // External data-loading boundary
    executeScript,
    isLoading,
    error,
    clearError,

    // Config management
    configHook,

    // Element state
    elementStateHook,
    data,
    showSelects,
    hasAttemptedLoad,
    hasProcessedData,
    isQueuedForExecution,
    setData,
    updateElementState,
    updateConfigState,
    markAttemptedLoad,
    setQueuedForExecution,
    resetElement,

    // Derived values
    convertedData,
    selectOptions,

    // Execution
    requestSequentialExecution,
    hasDataAndShouldRefresh,

    // Handler family
    handleRefresh,
    handleEdit,
    handleDelete,
    resetSelection,

    // Latest-render context (for adapter-level extras in the hook files)
    ctx,
  }
}

export default useElementBase
